/**
 * Admin view for the client-driven Adherence Rewards flow.
 * Rendered by IncentivesPanel when team === "adherence".
 *
 * Reuses the same visual containers (title + balance-shaped pill + Add btn,
 * team toggle, month/day filters, stat cards, records table) as the Cash
 * incentives panel so the two views feel like one product.
 *
 * Data endpoints:
 *   GET  /consultations/adherence/records?month&day&status&q
 *   PATCH /consultations/adherence/records/{id}  (approve)
 *   DELETE /consultations/adherence/records/{id}
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { Trash2, Copy, Pencil, X as XIcon } from "lucide-react";
import axios from "axios";
import { api } from "@/lib/consult/api";
import EmailBlastPanel from "./EmailBlastPanel";
import ErrorBoundary from "../../components/ErrorBoundary";
import TableColumnHeader from "./TableColumnHeader";
import { useTableView, applyTableView, uniqueValues, uniqueValuePairs, hasActiveFilters } from "./tableView";

const DARK_GREEN = "#0F3B2E";
const LABEL = "#2D5A4C";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";
const NAVY = "#1F3B5C";
const TEXT = "#1C2B26";
const RED = "#B4351B";
const SUCCESS = "#2E7D5B";
const ORANGE = "#E58A2E";

const fromYMD = (ymd) => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toYMD = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const money = (n) => new Intl.NumberFormat("en-CA", {
  style: "currency", currency: "CAD", minimumFractionDigits: 0,
}).format(n || 0);

function DatePickerButton({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const selected = value ? fromYMD(value) : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          style={{
            width: "100%", height: 38, padding: "0 12px",
            border: `1px solid ${BORDER}`, borderRadius: 8,
            background: "#fff", color: TEXT, fontSize: 14,
            textAlign: "left", cursor: "pointer",
          }}
        >
          {selected ? format(selected, "MMM d, yyyy") : "Pick a date"}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <Calendar
          mode="single"
          selected={selected || undefined}
          onSelect={(d) => {
            if (d) { onChange(toYMD(d)); setOpen(false); }
          }}
          className="p-0"
        />
      </PopoverContent>
    </Popover>
  );
}

// ── Filter strip removed: day+month selects now live inline in the combined
//    team+day+month strip (matches Cash + Expenses layout).

function StatCard({ label, value, sub, testId }) {
  return (
    <div data-testid={testId} style={{
      background: "#F9F8F6", border: `1px solid ${BORDER}`, borderRadius: 10,
      padding: "14px 16px", minWidth: 0,
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: ".16em",
        textTransform: "uppercase", color: MUTED,
      }}>{label}</div>
      <div style={{
        fontSize: 22, fontWeight: 800, color: DARK_GREEN, marginTop: 4,
        letterSpacing: "-.01em",
      }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: MUTED, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function StatusPill({ status, paidDate }) {
  const isPending = status === "pending";
  const label = isPending ? "Pending" : (paidDate ? `Paid ${format(fromYMD(paidDate) || new Date(), "MMM d")}` : "Approved");
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", padding: "3px 10px",
      borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: ".04em",
      background: isPending ? "rgba(229,138,46,0.10)" : "rgba(46,125,91,0.14)",
      color: isPending ? ORANGE : SUCCESS,
      border: `1px solid ${isPending ? "rgba(229,138,46,0.35)" : "rgba(46,125,91,0.35)"}`,
    }}>{label}</span>
  );
}

export default function AdherencePanel({
  team, setTeam, month, setMonth, day, setDay, months, monthLabel, refreshKey = 0,
  teams = null,
}) {
  const [records, setRecords] = useState([]);
  const [totals, setTotals] = useState({ records: 0, eft: 0, cash: 0, paid: 0, pending: 0 });
  const [loading, setLoading] = useState(false);
  // Sortable / filterable / searchable view (persisted per session) — the
  // same stacked-filter system the Cash rewards tables use.
  const [view, updateView] = useTableView("consult.adherence.view.v1");
  const [approveId, setApproveId] = useState(null);  const [payDate, setPayDate] = useState(toYMD(new Date()));
  const [payMethod, setPayMethod] = useState("eft");
  const [payAmount, setPayAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const approvePanelRef = useRef(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [milestoneOpts, setMilestoneOpts] = useState([]);
  const editPanelRef = useRef(null);

  useEffect(() => {
    // Milestones endpoint lives at /api/adherence/... (outside the
    // consultations baseURL of the shared `api` instance).
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/adherence/milestones`)
      .then((r) => setMilestoneOpts(r.data || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (day && day !== "all") params.day = day;
      const { data } = await api.get(`/adherence/records`, { params });
      setRecords(data.records || []);
      setTotals(data.totals || { records: 0, eft: 0, cash: 0, paid: 0, pending: 0 });
    } catch {
      toast.error("Failed to load adherence records.");
    } finally {
      setLoading(false);
    }
  }, [month, day]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const daysWithRecords = useMemo(() => {
    const seen = new Set();
    records.forEach((r) => r.date && seen.add(r.date));
    return Array.from(seen).sort().reverse();
  }, [records]);

  const fmtDate = useCallback((ymd) => {
    const d = fromYMD(ymd);
    return d ? format(d, "MMM d, yyyy") : (ymd || "");
  }, []);

  // Column model for the shared sort/filter/search system. Filters stack
  // across columns (AND), sort reorders the filtered rows.
  const typeOf = (r) => ((r.payment_method || r.type || "eft").toLowerCase() === "cash" ? "CASH" : "EFT");
  const statusOf = (r) => (r.status === "pending" ? "Pending" : "Approved");
  const tableColumns = useMemo(() => ({
    date:       { get: (r) => r.date, displayGet: (r) => fmtDate(r.date), type: "date" },
    time:       { get: (r) => r.updated_at || r.created_at || "", type: "date" },
    first_name: { get: (r) => r.first_name || "" },
    last_name:  { get: (r) => r.last_name || "" },
    milestone:  { get: (r) => r.milestone_label || r.milestone || "" },
    type:       { get: typeOf },
    amount:     { get: (r) => Number(r.amount) || 0, type: "number" },
    code:       { get: (r) => r.code_display || "" },
    status:     { get: statusOf },
    email:      { get: (r) => r.email || "" },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [fmtDate]);

  const displayRecords = useMemo(
    () => applyTableView(records, view, tableColumns,
      ["date", "first_name", "last_name", "milestone", "type", "code", "email", "status"]),
    [records, view, tableColumns]
  );

  const uniqDates      = useMemo(() => uniqueValuePairs(records, (r) => r.date, (r) => fmtDate(r.date)), [records, fmtDate]);
  const uniqFirstNames = useMemo(() => uniqueValues(records, (r) => r.first_name), [records]);
  const uniqLastNames  = useMemo(() => uniqueValues(records, (r) => r.last_name), [records]);
  const uniqMilestones = useMemo(() => uniqueValues(records, (r) => r.milestone_label || r.milestone), [records]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const uniqTypes      = useMemo(() => uniqueValues(records, typeOf), [records]);
  // Filter-by-value lists for the remaining columns so EVERY header dropdown
  // offers the same sort + filter experience.
  const uniqEmails     = useMemo(() => uniqueValues(records, (r) => r.email), [records]);
  const uniqCodes      = useMemo(() => uniqueValues(records, (r) => r.code_display), [records]);
  const uniqTimes      = useMemo(() => {
    const map = new Map();
    records.forEach((r) => {
      const iso = r.updated_at || r.created_at;
      if (!iso || map.has(iso)) return;
      const d = new Date(iso);
      map.set(iso, Number.isNaN(d.getTime()) ? iso : format(d, "h:mm a"));
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [records]);
  const uniqAmounts    = useMemo(() => {
    const map = new Map();
    records.forEach((r) => {
      const n = Number(r.amount) || 0;
      const key = String(n);
      if (!map.has(key)) map.set(key, { value: key, label: money(n) });
    });
    return Array.from(map.values()).sort((a, b) => Number(a.value) - Number(b.value));
  }, [records]);

  // True when a search/filter is active — cards should mirror what's on-screen.
  const viewActive = Boolean(hasActiveFilters(view) || (view.search && view.search.trim()));

  // Totals recomputed over the visible rows (used when a filter/search is on).
  const filteredTotals = useMemo(() => {
    const acc = { records: displayRecords.length, eft: 0, eft_count: 0, cash: 0, cash_count: 0, paid: 0, pending: 0 };
    displayRecords.forEach((r) => {
      const amt = Number(r.amount) || 0;
      if (r.status === "pending") { acc.pending += 1; return; }
      acc.paid += amt;
      if ((r.payment_method || "eft") === "cash") { acc.cash += amt; acc.cash_count += 1; }
      else { acc.eft += amt; acc.eft_count += 1; }
    });
    ["eft", "cash", "paid"].forEach((k) => { acc[k] = Math.round(acc[k] * 100) / 100; });
    return acc;
  }, [displayRecords]);

  const activeTotals = viewActive ? filteredTotals : {
    records: totals.records,
    eft: totals.eft,
    eft_count: records.filter((r) => r.status === "approved" && (r.payment_method || "eft") !== "cash").length,
    cash: totals.cash,
    cash_count: records.filter((r) => r.status === "approved" && (r.payment_method || "eft") === "cash").length,
    paid: totals.paid,
    pending: totals.pending,
  };

  const openApprove = (r) => {
    setEditId(null);
    setEditForm(null);
    setApproveId(r.id);
    setPayDate(toYMD(new Date()));
    setPayMethod(r.payment_method || "eft");
    setPayAmount(String(r.amount ?? ""));
    // Scroll the approve panel into view — on mobile it sits below the
    // records table, which can be far off-screen when clicked from a row
    // near the top.
    setTimeout(() => {
      approvePanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };
  const cancelApprove = () => setApproveId(null);

  // ── Admin edit (any field, even after approval) ──
  const openEdit = (r) => {
    setApproveId(null);
    setEditId(r.id);
    setEditForm({
      first_name: r.first_name || "",
      last_name: r.last_name || "",
      email: r.email || "",
      milestone: r.milestone || "",
      referred_person: r.referred_person || "",
      amount: String(r.amount ?? ""),
      payment_date: r.payment_date || toYMD(new Date()),
      payment_method: r.payment_method || "eft",
    });
    setTimeout(() => {
      editPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };
  const cancelEdit = () => { setEditId(null); setEditForm(null); };

  const saveEdit = async () => {
    if (!editId || !editForm || saving) return;
    const parsedAmount = Number(editForm.amount);
    if (editForm.amount === "" || Number.isNaN(parsedAmount) || parsedAmount < 0) {
      toast.error("Enter a valid amount (0 or more).");
      return;
    }
    if (!editForm.first_name.trim() || !editForm.last_name.trim()) {
      toast.error("First and last name are required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        email: editForm.email.trim(),
        milestone: editForm.milestone,
        amount: parsedAmount,
        referred_person: editForm.milestone === "friend_referral"
          ? (editForm.referred_person.trim() || null)
          : null,
      };
      if (editRow?.status === "approved") {
        body.payment_date = editForm.payment_date;
        body.payment_method = editForm.payment_method;
      }
      await api.patch(`/adherence/records/${editId}/edit`, body);
      toast.success("Record updated.");
      setEditId(null);
      setEditForm(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const submitApprove = async () => {
    if (!approveId || saving) return;
    const parsedAmount = Number(payAmount);
    if (payAmount === "" || Number.isNaN(parsedAmount) || parsedAmount < 0) {
      toast.error("Enter a valid amount (0 or more).");
      return;
    }
    setSaving(true);
    try {
      await api.patch(
        `/adherence/records/${approveId}`,
        { payment_date: payDate, payment_method: payMethod, amount: parsedAmount },
      );
      toast.success("Request approved.");
      setApproveId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to approve.");
    } finally {
      setSaving(false);
    }
  };

  const removeRecord = async (id) => {
    if (!window.confirm("Delete this adherence record? This cannot be undone.")) return;
    try {
      await api.delete(`/adherence/records/${id}`);
      toast.success("Record deleted.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete.");
    }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/patient`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Client link copied to clipboard.");
    } catch {
      toast.error(`Copy manually: ${url}`);
    }
  };

  const approveRow = records.find((r) => r.id === approveId);
  const editRow = records.find((r) => r.id === editId);

  return (
    <div data-testid="adherence-panel" style={{
      background: "#fff", borderRadius: 14, border: `1px solid ${BORDER}`, padding: 20,
    }}>
      {/* Local styles — share the same team-month strip look */}
      <style>{`
        .inc-team-month {
          display: flex; align-items: stretch; border: 1px solid #e2e8f0;
          background: #fff; border-radius: 6px; overflow: hidden; width: 100%;
        }
        .inc-team-btn {
          flex: 1; height: 36px; padding: 0 12px; font-size: 12px; font-weight: 600;
          color: #64748b; background: #fff; border: 0;
          border-right: 1px solid #e2e8f0; cursor: pointer;
        }
        .inc-team-btn.active { background: ${NAVY}; color: #fff; }
        .inc-day-select, .inc-month-select {
          flex: 1; height: 36px; padding: 0 30px 0 12px; font-size: 12px; font-weight: 600;
          color: #fff; background: ${NAVY}; border: 0; text-align: center;
          -webkit-appearance: none; appearance: none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='white'><path fill-rule='evenodd' d='M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.06l3.71-3.83a.75.75 0 1 1 1.08 1.04l-4.25 4.38a.75.75 0 0 1-1.08 0L5.21 8.27a.75.75 0 0 1 .02-1.06z' clip-rule='evenodd'/></svg>");
          background-repeat: no-repeat; background-position: right 10px center; background-size: 14px;
          cursor: pointer;
        }
        .inc-day-select { border-right: 1px solid rgba(255,255,255,0.20); }
        @media (max-width: 640px) {
          .inc-team-month { flex-wrap: wrap; }
          /* Row 1 = Team 1 | Team 2 | Team 3 | HQ (four even columns),
             Row 2 = All days | Month (two halves). */
          .inc-team-btn { flex: 1 1 25%; border-bottom: 1px solid #e2e8f0; font-size: 11px; padding: 0 4px; white-space: nowrap; }
          .inc-team-btn:last-of-type { border-right: 0; }
          .inc-day-select, .inc-month-select { flex: 1 1 50%; }
        }
        .adh-stats {
          grid-template-columns: repeat(4, 1fr);
        }
        .adh-approve-fields {
          grid-template-columns: 220px 1fr;
        }
        @media (max-width: 720px) {
          .adh-stats {
            grid-template-columns: 1fr 1fr;
          }
          .adh-approve-fields {
            grid-template-columns: 1fr !important;
          }
        }
        .adh-tbl { width: 100%; border-collapse: collapse; min-width: 900px; font-size: 13px; }
        .adh-tbl th { text-align: left; font-size: 10px; letter-spacing: .12em;
          text-transform: uppercase; color: ${MUTED}; font-weight: 700;
          padding: 8px 10px; border-bottom: 1px solid ${BORDER}; white-space: nowrap; }
        .adh-tbl td { padding: 10px; border-bottom: 1px solid ${BORDER}; color: ${TEXT};
          vertical-align: middle; white-space: nowrap; }
        .adh-tbl tr:hover td { background: #FAFAF7; }
        .adh-icon-btn { background: none; border: none; cursor: pointer;
          color: ${MUTED}; padding: 4px; margin-left: 2px; }
        .adh-icon-btn:hover { color: ${DARK_GREEN}; }
        .adh-icon-btn.danger:hover { color: ${RED}; }
      `}</style>

      {/* Title + pending pill + copy-link button (matches Cash layout — always the top row) */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 12, marginBottom: 14, flexWrap: "wrap",
      }}>
        <h2 style={{
          fontFamily: "'Work Sans', system-ui, sans-serif",
          fontSize: 18, fontWeight: 700, margin: 0,
        }}>Adherence</h2>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
          <div title="Pending approvals" style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 12px", borderRadius: 8,
            background: "#fff", color: ORANGE,
            border: `1px solid rgba(229,138,46,0.35)`,
            fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
          }} data-testid="adh-pending-count">
            {activeTotals.pending} pending
          </div>
          <button
            type="button"
            data-testid="adh-copy-link-btn"
            onClick={copyLink}
            title="Copy the public /patient link for clients"
            style={{
              background: DARK_GREEN, color: "#fff", border: 0,
              borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600,
              display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
            }}
          >
            <Copy style={{ width: 14, height: 14 }} strokeWidth={2.5} />
            Copy public link
          </button>
        </div>
      </div>

      {/* Combined Team + Day + Month strip (identical shape to Incentives/Expenses) */}
      <div className="inc-team-month" data-testid="adh-team-month" style={{ marginBottom: 14 }}>
        {(teams && teams.length ? teams : [
          { key: "team_1", label: "Team 1 (G)" },
          { key: "team_2", label: "Team 2 (R)" },
          { key: "team_3", label: "Team 3 (M)" },
        ]).map((t, i, arr) => (
          <button
            key={t.key}
            type="button"
            data-testid={`adh-team-${t.key}`}
            onClick={() => setTeam(t.key)}
            className={`inc-team-btn${team === t.key ? " active" : ""}`}
            style={i === arr.length - 1 ? { borderRight: "1px solid #e2e8f0" } : undefined}
          >{t.label}</button>
        ))}
        <button
          type="button"
          data-testid="adh-team-hq"
          onClick={() => setTeam("adherence")}
          className={`inc-team-btn${team === "adherence" ? " active" : ""} hq`}
        >HQ</button>
        <select
          data-testid="adh-day-select"
          className="inc-day-select"
          value={day}
          onChange={(e) => setDay(e.target.value)}
        >
          <option value="all" style={{ color: "#000" }}>All days</option>
          {(daysWithRecords || []).map((d) => (
            <option key={d} value={d} style={{ color: "#000" }}>
              {fromYMD(d) ? format(fromYMD(d), "MMM d") : d}
            </option>
          ))}
        </select>
        <select
          data-testid="adh-month-select"
          className="inc-month-select"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        >
          {months.map((m) => (
            <option key={m.key} value={m.key} style={{ color: "#000" }}>{m.label}</option>
          ))}
        </select>
      </div>

      {/* Explainer strip */}
      <div style={{
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        background: "#F9F8F6", border: `1px solid ${BORDER}`, borderRadius: 10,
        padding: "10px 14px", marginBottom: 14, fontSize: 12.5, color: MUTED,
      }}>
        Clients can submit their own request at
        <b style={{
          color: DARK_GREEN, fontWeight: 800,
          fontFamily: "'JetBrains Mono','Menlo',monospace",
        }}>hepcure.ca/patient</b>
      </div>

      {/* Admin email-blast — always visible for authenticated staff on /outreach.
          Uses the master-PIN session token to authenticate. */}
      <ErrorBoundary label="Email broadcast">
        <EmailBlastPanel />
      </ErrorBoundary>

      {/* Stat cards */}
      <div style={{
        display: "grid", gap: 8, marginBottom: 14,
      }} className="adh-stats">
        <StatCard testId="adh-stat-records" label="Records" value={activeTotals.records}
          sub={viewActive ? "Filtered view" : "This month"} />
        <StatCard testId="adh-stat-eft" label="EFT" value={money(activeTotals.eft)}
          sub={`${activeTotals.eft_count} records`} />
        <StatCard testId="adh-stat-cash" label="Cash" value={money(activeTotals.cash)}
          sub={`${activeTotals.cash_count} records`} />
        <StatCard testId="adh-stat-total-paid" label="Total paid" value={money(activeTotals.paid)}
          sub={viewActive ? "Filtered view" : monthLabel} />
      </div>

      {/* Records table */}
      <div style={{
        overflowX: "auto", WebkitOverflowScrolling: "touch",
        margin: "0 -20px", padding: "0 20px",
      }}>
        {loading && records.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: MUTED }}>Loading…</div>
        ) : records.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: MUTED }}>
            No adherence records yet for this month.
          </div>
        ) : (
          <>
            <div style={{
              display: "flex", alignItems: "center", gap: 8, padding: "0 0 8px",
              flexWrap: "nowrap",
            }}>
              <input
                data-testid="adh-search"
                type="text"
                value={view.search || ""}
                onChange={(e) => updateView({ search: e.target.value })}
                placeholder="Search name, milestone, code…"
                style={{
                  flex: 1, minWidth: 0, height: 34,
                  padding: "0 10px", fontSize: 13,
                  border: `1px solid ${BORDER}`, borderRadius: 8,
                  background: "#fff", color: TEXT, boxSizing: "border-box",
                }}
              />
              {(view.sortKey || hasActiveFilters(view) || view.search) && (
                <button
                  type="button"
                  data-testid="adh-view-clear"
                  onClick={() => updateView({ sortKey: null, sortDir: null, filters: {}, search: "" })}
                  style={{
                    background: "rgba(180,53,27,0.08)", border: `1px solid #E9BFB1`,
                    borderRadius: 8, padding: "6px 12px",
                    fontSize: 12, fontWeight: 700, color: "#B4351B", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                >
                  <XIcon style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                  Clear all filters
                </button>
              )}
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
                <div data-testid="adh-shown-count" style={{ fontSize: 12, color: MUTED, whiteSpace: "nowrap" }}>
                  {displayRecords.length} of {records.length}
                </div>
                <button
                  type="button"
                  data-testid="adh-copy-selection"
                  title="Copy the currently visible rows (date · full name · amount) to your clipboard"
                  onClick={async () => {
                    if (displayRecords.length === 0) {
                      toast.error("Nothing to copy — no visible rows.");
                      return;
                    }
                    let running = 0;
                    const lines = displayRecords.map((r) => {
                      const dateStr = fromYMD(r.date) ? format(fromYMD(r.date), "MMM d, yyyy") : (r.date || "");
                      const name = `${(r.first_name || "").trim()} ${(r.last_name || "").trim()}`.trim() || "—";
                      const amt = Number(r.amount) || 0;
                      running += amt;
                      return `${dateStr}\t${name}\t${money(amt)}`;
                    });
                    lines.push("");
                    lines.push(`Total\t${displayRecords.length} record${displayRecords.length === 1 ? "" : "s"}\t${money(Math.round(running * 100) / 100)}`);
                    const text = lines.join("\n");
                    try {
                      await navigator.clipboard.writeText(text);
                      toast.success(`Copied ${displayRecords.length} row${displayRecords.length === 1 ? "" : "s"} + total to clipboard.`);
                    } catch {
                      toast.error("Couldn't access clipboard — copy manually from the table.");
                    }
                  }}
                  style={{
                    background: "#fff", border: `1px solid ${BORDER}`,
                    borderRadius: 8, padding: "4px 10px",
                    fontSize: 12, fontWeight: 700, color: DARK_GREEN, cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
                  }}
                >
                  <Copy style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                  Copy
                </button>
              </div>
            </div>
          <table className="adh-tbl" data-testid="adh-table">
            <thead>
              <tr>
                <TableColumnHeader columnKey="date" label="Date" type="date"
                  values={uniqDates} view={view} onChange={updateView} testId="adherence" />
                <TableColumnHeader columnKey="time" label="Time" type="date"
                  values={uniqTimes} view={view} onChange={updateView} testId="adherence" />
                <TableColumnHeader columnKey="first_name" label="First"
                  values={uniqFirstNames} view={view} onChange={updateView} testId="adherence" />
                <TableColumnHeader columnKey="last_name" label="Last"
                  values={uniqLastNames} view={view} onChange={updateView} testId="adherence" />
                <TableColumnHeader columnKey="milestone" label="Milestone"
                  values={uniqMilestones} view={view} onChange={updateView} testId="adherence" />
                <TableColumnHeader columnKey="type" label="Type"
                  values={uniqTypes} view={view} onChange={updateView} testId="adherence" />
                <TableColumnHeader columnKey="amount" label="Amount" type="number" align="right"
                  values={uniqAmounts} view={view} onChange={updateView} testId="adherence" />
                <TableColumnHeader columnKey="code" label="Ref #"
                  values={uniqCodes} view={view} onChange={updateView} testId="adherence" />
                <TableColumnHeader columnKey="status" label="Status"
                  values={["Pending", "Approved"]} view={view} onChange={updateView} testId="adherence" />
                <TableColumnHeader columnKey="email" label="Email"
                  values={uniqEmails} view={view} onChange={updateView} testId="adherence" />
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayRecords.length === 0 && (
                <tr data-testid="adh-no-matches">
                  <td colSpan={11} style={{ padding: 24, textAlign: "center", color: MUTED }}>
                    No records match your search.
                  </td>
                </tr>
              )}
              {displayRecords.map((r) => (
                <tr key={r.id} data-testid={`adh-row-${r.id}`}>
                  <td>{fromYMD(r.date) ? format(fromYMD(r.date), "MMM d, yyyy") : r.date}</td>
                  <td style={{ color: MUTED, fontSize: 12.5, whiteSpace: "nowrap" }}
                      data-testid={`adh-time-${r.id}`}
                      title={r.updated_at && r.updated_at !== r.created_at ? "Revised time" : "Submitted time"}>
                    {(() => {
                      const iso = r.updated_at || r.created_at;
                      if (!iso) return "—";
                      const d = new Date(iso);
                      return Number.isNaN(d.getTime()) ? "—" : format(d, "h:mm a");
                    })()}
                  </td>
                  <td>{r.first_name}</td>
                  <td>{r.last_name}</td>
                  <td>
                    {r.milestone_label || r.milestone}
                    {r.referred_person && (
                      <span style={{ color: MUTED, marginLeft: 6, fontSize: 12.5 }}>
                        · {r.referred_person}
                      </span>
                    )}
                  </td>
                  <td>
                    <span style={{
                      display: "inline-block", padding: "2px 8px", borderRadius: 999,
                      fontSize: 11, fontWeight: 700,
                      background: (r.payment_method || r.type) === "cash"
                        ? "rgba(46,125,91,0.10)" : "rgba(74,111,176,0.10)",
                      color: (r.payment_method || r.type) === "cash"
                        ? "#2E7D5B" : "#4A6FB0",
                    }}>{((r.payment_method || r.type || "eft").toUpperCase())}</span>
                  </td>
                  <td style={{ textAlign: "right", fontWeight: 700 }}>{money(r.amount)}</td>
                  <td style={{ fontFamily: "'JetBrains Mono','Menlo',monospace", fontSize: 12 }}>
                    {r.code_display}
                  </td>
                  <td><StatusPill status={r.status} paidDate={r.payment_date} /></td>
                  <td style={{ color: MUTED, fontSize: 12.5 }}>{r.email}</td>
                  <td style={{ textAlign: "right" }}>
                    {r.status === "pending" ? (
                      <button
                        type="button"
                        onClick={() => openApprove(r)}
                        data-testid={`adh-approve-${r.id}`}
                        style={{
                          background: DARK_GREEN, color: "#fff", border: 0,
                          borderRadius: 6, padding: "4px 10px", fontSize: 12,
                          fontWeight: 600, cursor: "pointer",
                        }}
                      >Approve</button>
                    ) : (
                      <span style={{ color: SUCCESS, fontSize: 18 }} title="Approved">✓</span>
                    )}
                    <button
                      className="adh-icon-btn"
                      onClick={() => openEdit(r)}
                      title="Edit"
                      data-testid={`adh-edit-${r.id}`}
                    ><Pencil style={{ width: 14, height: 14 }} /></button>
                    <button
                      className="adh-icon-btn danger"
                      onClick={() => removeRecord(r.id)}
                      title="Delete"
                      data-testid={`adh-del-${r.id}`}
                    ><Trash2 style={{ width: 14, height: 14 }} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {/* Approve panel — separate card below so mobile stays clean */}
      {approveId && approveRow && (
        <div ref={approvePanelRef} style={{
          marginTop: 16, padding: 20, background: "#FBFAF7",
          border: `1px solid ${BORDER}`, borderRadius: 12,
        }} data-testid="adh-approve-panel">
          <div style={{
            fontSize: 11, fontWeight: 800, color: DARK_GREEN,
            letterSpacing: ".16em", textTransform: "uppercase",
          }}>Approve · {approveRow.code_display}</div>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 4, fontWeight: 700 }} data-testid="adh-approve-patient-name">
            {approveRow.first_name} {approveRow.last_name}
          </div>
          <div style={{ fontSize: 13, color: MUTED, marginTop: 2 }} data-testid="adh-approve-reason-amount">
            {approveRow.milestone_label} · requested {money(approveRow.amount)}
          </div>

          <div style={{ marginTop: 16, maxWidth: 640 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
              textTransform: "uppercase", color: LABEL, marginBottom: 6,
            }}>Amount to pay</div>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 0,
              border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden",
              background: "#fff",
            }}>
              <span style={{
                padding: "0 10px", height: 36, display: "flex", alignItems: "center",
                background: "#F4F3EF", color: MUTED, fontWeight: 700, fontSize: 13,
                borderRight: `1px solid ${BORDER}`,
              }}>$</span>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="1"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                data-testid="adh-approve-amount"
                style={{
                  height: 36, width: 140, border: 0, padding: "0 10px",
                  fontSize: 14, fontWeight: 700, color: TEXT, outline: "none",
                  fontFamily: "inherit", background: "transparent",
                }}
              />
            </div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 6 }}>
              Override the milestone default if this client is entitled to a different amount.
            </div>
          </div>

          <div style={{
            display: "grid", gridTemplateColumns: "220px 1fr",
            gap: 14, marginTop: 16, maxWidth: 640, alignItems: "end",
          }} className="adh-approve-fields">
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                textTransform: "uppercase", color: LABEL, marginBottom: 6,
              }}>Payment date</div>
              <DatePickerButton value={payDate} onChange={setPayDate} testId="adh-approve-date" />
            </div>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                textTransform: "uppercase", color: LABEL, marginBottom: 6,
              }}>Payment method</div>
              <div style={{
                display: "inline-flex", padding: 4, background: "#F4F3EF",
                borderRadius: 8, gap: 2,
              }}>
                {[
                  { k: "eft", label: "EFT / e-transfer" },
                  { k: "cash", label: "Cash" },
                ].map((o) => (
                  <button
                    key={o.k}
                    type="button"
                    onClick={() => setPayMethod(o.k)}
                    data-testid={`adh-approve-method-${o.k}`}
                    style={{
                      background: payMethod === o.k ? DARK_GREEN : "transparent",
                      color: payMethod === o.k ? "#fff" : MUTED,
                      border: 0, padding: "6px 12px", height: 32, fontSize: 12,
                      fontWeight: 700, borderRadius: 6, cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >{o.label}</button>
                ))}
              </div>
            </div>
          </div>

          <div style={{ fontSize: 12.5, color: MUTED, marginTop: 14, lineHeight: 1.55, maxWidth: 640 }}>
            Once approved, the record locks — client-side edits and deletes are disabled and the
            payment date will be visible when the client enters their code.
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button
              type="button"
              onClick={cancelApprove}
              style={{
                background: "#fff", color: TEXT, border: `1px solid ${BORDER}`,
                borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >Cancel</button>
            <button
              type="button"
              onClick={submitApprove}
              disabled={saving}
              data-testid="adh-approve-confirm"
              style={{
                background: DARK_GREEN, color: "#fff", border: 0, borderRadius: 8,
                padding: "8px 18px", fontSize: 13, fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1,
                fontFamily: "inherit",
              }}
            >{saving ? "Saving…" : "Confirm approval"}</button>
          </div>
        </div>
      )}

      {/* Edit panel — admin can correct any field, even after approval */}
      {editId && editRow && editForm && (
        <div ref={editPanelRef} style={{
          marginTop: 16, padding: 20, background: "#FBFAF7",
          border: `1px solid ${BORDER}`, borderRadius: 12,
        }} data-testid="adh-edit-panel">
          <div style={{
            fontSize: 11, fontWeight: 800, color: NAVY,
            letterSpacing: ".16em", textTransform: "uppercase",
          }}>Edit · {editRow.code_display} · {editRow.status === "approved" ? "Approved record" : "Pending request"}</div>

          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: 14, marginTop: 14, maxWidth: 640,
          }} className="adh-approve-fields">
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                textTransform: "uppercase", color: LABEL, marginBottom: 6,
              }}>First name</div>
              <input
                type="text"
                value={editForm.first_name}
                onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                data-testid="adh-edit-first-name"
                style={{
                  width: "100%", height: 38, padding: "0 12px",
                  border: `1px solid ${BORDER}`, borderRadius: 8,
                  fontSize: 14, color: TEXT, fontFamily: "inherit",
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                textTransform: "uppercase", color: LABEL, marginBottom: 6,
              }}>Last name</div>
              <input
                type="text"
                value={editForm.last_name}
                onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                data-testid="adh-edit-last-name"
                style={{
                  width: "100%", height: 38, padding: "0 12px",
                  border: `1px solid ${BORDER}`, borderRadius: 8,
                  fontSize: 14, color: TEXT, fontFamily: "inherit",
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                textTransform: "uppercase", color: LABEL, marginBottom: 6,
              }}>Email</div>
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="adh-edit-email"
                style={{
                  width: "100%", height: 38, padding: "0 12px",
                  border: `1px solid ${BORDER}`, borderRadius: 8,
                  fontSize: 14, color: TEXT, fontFamily: "inherit",
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                textTransform: "uppercase", color: LABEL, marginBottom: 6,
              }}>Milestone</div>
              <select
                value={editForm.milestone}
                onChange={(e) => {
                  const key = e.target.value;
                  const opt = milestoneOpts.find((m) => m.key === key);
                  setEditForm({
                    ...editForm,
                    milestone: key,
                    amount: opt ? String(opt.amount) : editForm.amount,
                  });
                }}
                data-testid="adh-edit-milestone"
                style={{
                  width: "100%", height: 38, padding: "0 10px",
                  border: `1px solid ${BORDER}`, borderRadius: 8,
                  fontSize: 13.5, color: TEXT, fontFamily: "inherit",
                  background: "#fff", outline: "none", boxSizing: "border-box",
                }}
              >
                {milestoneOpts.map((m) => (
                  <option key={m.key} value={m.key}>{m.label} — ${m.amount}</option>
                ))}
              </select>
            </div>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                textTransform: "uppercase", color: LABEL, marginBottom: 6,
              }}>Amount</div>
              <div style={{
                display: "inline-flex", alignItems: "center",
                border: `1px solid ${BORDER}`, borderRadius: 8, overflow: "hidden",
                background: "#fff", width: "100%",
              }}>
                <span style={{
                  padding: "0 10px", height: 38, display: "flex", alignItems: "center",
                  background: "#F4F3EF", color: MUTED, fontWeight: 700, fontSize: 13,
                  borderRight: `1px solid ${BORDER}`,
                }}>$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="1"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  data-testid="adh-edit-amount"
                  style={{
                    height: 38, width: "100%", border: 0, padding: "0 10px",
                    fontSize: 14, fontWeight: 700, color: TEXT, outline: "none",
                    fontFamily: "inherit", background: "transparent",
                  }}
                />
              </div>
            </div>
            {editForm.milestone === "friend_referral" && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                  textTransform: "uppercase", color: LABEL, marginBottom: 6,
                }}>Referred friend&apos;s name</div>
                <input
                  type="text"
                  value={editForm.referred_person}
                  onChange={(e) => setEditForm({ ...editForm, referred_person: e.target.value })}
                  data-testid="adh-edit-referred-person"
                  style={{
                    width: "100%", height: 38, padding: "0 12px",
                    border: `1px solid ${BORDER}`, borderRadius: 8,
                    fontSize: 14, color: TEXT, fontFamily: "inherit",
                    outline: "none", boxSizing: "border-box",
                  }}
                />
              </div>
            )}
            {editRow.status === "approved" && (
              <>
                <div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                    textTransform: "uppercase", color: LABEL, marginBottom: 6,
                  }}>Payment date</div>
                  <DatePickerButton
                    value={editForm.payment_date}
                    onChange={(v) => setEditForm({ ...editForm, payment_date: v })}
                    testId="adh-edit-payment-date"
                  />
                </div>
                <div>
                  <div style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: ".14em",
                    textTransform: "uppercase", color: LABEL, marginBottom: 6,
                  }}>Payment method</div>
                  <div style={{
                    display: "inline-flex", padding: 4, background: "#F4F3EF",
                    borderRadius: 8, gap: 2,
                  }}>
                    {[
                      { k: "eft", label: "EFT / e-transfer" },
                      { k: "cash", label: "Cash" },
                    ].map((o) => (
                      <button
                        key={o.k}
                        type="button"
                        onClick={() => setEditForm({ ...editForm, payment_method: o.k })}
                        data-testid={`adh-edit-method-${o.k}`}
                        style={{
                          background: editForm.payment_method === o.k ? DARK_GREEN : "transparent",
                          color: editForm.payment_method === o.k ? "#fff" : MUTED,
                          border: 0, padding: "6px 12px", height: 32, fontSize: 12,
                          fontWeight: 700, borderRadius: 6, cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >{o.label}</button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={{ fontSize: 12.5, color: MUTED, marginTop: 14, lineHeight: 1.55, maxWidth: 640 }}>
            Changing the milestone resets the amount to that milestone&apos;s default — adjust the amount after switching if needed.
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button
              type="button"
              onClick={cancelEdit}
              data-testid="adh-edit-cancel"
              style={{
                background: "#fff", color: TEXT, border: `1px solid ${BORDER}`,
                borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >Cancel</button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={saving}
              data-testid="adh-edit-save"
              style={{
                background: NAVY, color: "#fff", border: 0, borderRadius: 8,
                padding: "8px 18px", fontSize: 13, fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1,
                fontFamily: "inherit",
              }}
            >{saving ? "Saving…" : "Save changes"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
